import React from 'react';
import ReactRender from '../tools/react-render';
import { isTeamUpLoggedIn } from './TeamUp/API/Utilities';

const SiteMenuEl = document.querySelector('[data-behaviour="site-menu"]');
if (SiteMenuEl) {
    const SiteMenu = React.lazy(() => import('./SiteMenu'));
    const menuId = SiteMenuEl.getAttribute('data-menu-id') ?? '';
    const menuIdLoggedIn =
        SiteMenuEl.getAttribute('data-menu-id-logged-in') ?? menuId;
    ReactRender(
        <SiteMenu menuId={menuId} menuIdLoggedIn={menuIdLoggedIn} />,
        SiteMenuEl,
    );
}

const ListingEl = document.querySelector('[data-behaviour="store-listing"]');
if (ListingEl) {
    const Listing = React.lazy(() => import('./Store/Listing'));
    let config = JSON.parse(ListingEl.getAttribute('data-config') ?? '{}');
    ReactRender(<Listing config={config} />, ListingEl);
}

const ProductEl = document.querySelector('[data-behaviour="store-product"]');
if (ProductEl) {
    const Product = React.lazy(() => import('./Store/Product'));
    let handle = ProductEl.getAttribute('data-handle') ?? null;
    let queryOptions = ProductEl.getAttribute('data-options')
        ? ProductEl.getAttribute('data-options').split(',')
        : null;
    ReactRender(
        <Product handle={handle} queryOptions={queryOptions} />,
        ProductEl,
    );
}

const BasketEl = document.querySelector('[data-behaviour="store-basket"]');
if (BasketEl) {
    const Basket = React.lazy(() => import('./Store/Basket'));
    let appView = BasketEl.getAttribute('data-app-view') ?? null;
    ReactRender(<Basket appView={appView} />, BasketEl);
}

const AccountEl = document.querySelector('[data-behaviour="my-account"]');
if (AccountEl) {
    const Account = React.lazy(() => import('./Account/Account'));
    const searchParams = new URLSearchParams(window.location.search);
    const tab = searchParams.has('tab') ? searchParams.get('tab') : false;
    ReactRender(<Account tab={tab} />, AccountEl);
}

const StudioListingEl = document.querySelector(
    '[data-behaviour="studio-listing"]',
);
if (StudioListingEl) {
    const StudioListing = React.lazy(() => import('./Studios/Listing'));
    ReactRender(<StudioListing />, StudioListingEl);
}

const SignUpEl = document.querySelector('[data-behaviour="signup"]');
if (SignUpEl) {
    const SignUpWrapper = React.lazy(() => import('./Account/SignUpWrapper'));
    ReactRender(<SignUpWrapper />, SignUpEl);
}

const SignInEl = document.querySelector('[data-behaviour="signin"]');
if (SignInEl) {
    const Signin = React.lazy(() => import('./Account/Signin'));
    ReactRender(<Signin />, SignInEl);
}

const hotpodHomeListings = document.querySelectorAll(
    '[data-behaviour="hotpodhome-listing"]',
);
if (hotpodHomeListings.length > 0) {
    const HotpodHome = React.lazy(() => import('./HotpodHome'));
    hotpodHomeListings.forEach((hotpodHomeListing) => {
        let config = JSON.parse(
            hotpodHomeListing.getAttribute('data-config') ?? '{}',
        );
        ReactRender(<HotpodHome config={config} />, hotpodHomeListing);
    });
}

const hotpodHomePricingTableButtons = document.querySelectorAll(
    '[data-behaviour="hotpodhome-pricing-table-button"]',
);
if (hotpodHomePricingTableButtons.length > 0) {
    const PricingTableButton = React.lazy(
        () => import('./HotpodHome/PricingTableButton'),
    );
    hotpodHomePricingTableButtons.forEach((hotpodHomePricingTableBtn) => {
        const ctaTitle =
            hotpodHomePricingTableBtn.getAttribute('data-cta-title') ?? '';
        const membershipId =
            hotpodHomePricingTableBtn.getAttribute('data-membership-id') ?? '';
        const paymentPlanId =
            hotpodHomePricingTableBtn.getAttribute('data-payment-plan-id') ??
            '';
        ReactRender(
            <PricingTableButton
                ctaTitle={ctaTitle}
                membershipId={membershipId}
                paymentPlanId={paymentPlanId}
            />,
            hotpodHomePricingTableBtn,
        );
    });
}

const sessionsBlocks = document.querySelectorAll('[data-behaviour="sessions"]');
if (sessionsBlocks.length > 0) {
    const Sessions = React.lazy(() => import('./Sessions'));
    sessionsBlocks.forEach((sessionBlock) => {
        const ids = sessionBlock.getAttribute('data-sessions').split(',') ?? '';
        ReactRender(<Sessions ids={ids} />, sessionBlock);
    });
}

const studioPortalEl = document.querySelector(
    '[data-behaviour="react-studio-portal"]',
);

if (studioPortalEl) {
    const StudioPortal = React.lazy(() => import('./Portals/StudioPortal'));
    const studioProviderId =
        studioPortalEl.getAttribute('data-studio-provider-id') ?? '';

    ReactRender(
        <StudioPortal studioProviderId={studioProviderId} />,
        studioPortalEl,
    );
}

// If the user is logged in, set up a background token refresh every 10 minutes to help
// prevent refresh token expiration issues.
if (isTeamUpLoggedIn()) {
    setInterval(
        async () => {
            try {
                const baseRequestModule = await import(
                    './TeamUp/API/Requests/BaseRequest'
                );
                const BaseRequest = baseRequestModule.default;
                const tokenManagerModule = await import(
                    './TeamUp/API/TokenManager'
                );
                const TokenManager = tokenManagerModule.TokenManager;

                await TokenManager.refreshTokenIfNeeded(BaseRequest);

                console.log('Background token refresh successful');
            } catch (err) {
                console.warn('Background token refresh failed', err);
            }
        },
        10 * 60 * 1000,
    );
}

const PopupsEl = document.querySelector('[data-behaviour="popups"]');
if (PopupsEl) {
    const Popups = React.lazy(() => import('./Popups'));
    const id = PopupsEl.getAttribute('data-id');
    const theme = PopupsEl.getAttribute('data-theme');
    ReactRender(<Popups id={id} theme={theme} />, PopupsEl);
}

// Gift card redeem page, /studios/<slug>/redeem-gift-card/
const GiftCardRedeemEl = document.querySelector(
    '[data-behaviour="gift-card-redeem"]',
);
if (GiftCardRedeemEl) {
    const RedeemGiftCard = React.lazy(() => import('./GiftCards/RedeemGiftCard'));
    const attr = (name) => GiftCardRedeemEl.getAttribute(name) ?? '';
    ReactRender(
        <RedeemGiftCard
            studioId={attr('data-studio-id')}
            studioName={attr('data-studio-name')}
            studioSlug={attr('data-studio-slug')}
            trackingName={attr('data-tracking-studio-name')}
            providerId={attr('data-provider-id')}
            studioEmail={attr('data-studio-email')}
            pricesUrl={attr('data-prices-url')}
            scheduleUrl={attr('data-schedule-url')}
            finderUrl={attr('data-finder-url')}
            live={attr('data-live') === '1'}
            allowTestKeys={attr('data-test-keys') === '1'}
        />,
        GiftCardRedeemEl,
    );
}

// "Redeem a gift card" page (template-redeem-gift-card.php)
const GiftCardFinderEl = document.querySelector(
    '[data-behaviour="gift-card-finder"]',
);
if (GiftCardFinderEl) {
    const GiftCardFinder = React.lazy(() => import('./GiftCards/GiftCardFinder'));
    let studios = [];
    try {
        studios = JSON.parse(GiftCardFinderEl.getAttribute('data-studios') ?? '[]');
    } catch (error) {
        studios = [];
    }
    ReactRender(
        <GiftCardFinder
            studios={Array.isArray(studios) ? studios : []}
            allowTestKeys={GiftCardFinderEl.getAttribute('data-test-keys') === '1'}
        />,
        GiftCardFinderEl,
    );
}
